import { type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes } from 'react'

type CookieButtonVariant = 'primary' | 'secondary' | 'ghost'

interface CookieButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: CookieButtonVariant
}

export function CookieButton({
  variant = 'secondary',
  className = '',
  children,
  ...props
}: CookieButtonProps) {
  const base =
    'inline-flex items-center justify-center rounded-[11px] px-5 py-2.5 text-[14px] font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sp-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sp-surface)] disabled:cursor-not-allowed disabled:opacity-50'

  const variants: Record<CookieButtonVariant, string> = {
    primary:
      'text-white shadow-[0_10px_28px_-8px_var(--sp-cta-shadow)] hover:brightness-110 active:scale-[0.98]',
    secondary: 'hover:bg-[var(--sp-surface-2)] active:scale-[0.98]',
    ghost: 'hover:bg-[var(--sp-surface-2)] active:scale-[0.98]',
  }

  const styles: Record<CookieButtonVariant, CSSProperties> = {
    primary: { background: 'var(--sp-cta)' },
    secondary: {
      border: '1px solid var(--sp-border)',
      background: 'var(--sp-surface)',
      color: 'var(--sp-text)',
    },
    ghost: {
      border: '1px solid transparent',
      background: 'transparent',
      color: 'var(--sp-muted)',
    },
  }

  return (
    <button
      type="button"
      className={`${base} ${variants[variant]} ${className}`}
      style={styles[variant]}
      {...props}
    >
      {children}
    </button>
  )
}

interface CookieSwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
}

export function CookieSwitch({ label, id, checked, disabled, onChange, ...props }: CookieSwitchProps) {
  return (
    <label htmlFor={id} className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-checked={checked}
        {...props}
      />
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:duration-200 after:content-[''] peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--sp-blue)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--sp-surface)] peer-disabled:cursor-not-allowed peer-disabled:opacity-50 ${
          checked ? 'bg-[var(--sp-blue)]' : 'bg-[var(--sp-border)]'
        }`}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </label>
  )
}
